import { ScrollText } from "lucide-react";
import {
  PageHeading, Section, Field, Steps, Callout, RoleBadge,
  Diagram, MockSelect, MockField, MockButton, MockTable, MockRow,
} from "../shared";

export default function SettingsSystemLogSection() {
  return (
    <div data-testid="guide-settings-system-log">
      <PageHeading
        icon={ScrollText}
        title="System Diagnostics Log"
        description="One place that records the problems and important events the system runs into — failed emails, sign-in trouble, database hiccups, crashes — so the IT person has the details they need to fix them."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="it_admin" />
        <span className="text-xs text-white/40">Needs the <span className="font-mono text-white/70">view_system_logs</span> permission. Bulk-deleting also asks for your password first.</span>
      </div>

      <Callout variant="info" title="In plain words">
        Think of this as the system's diary of things that went wrong. You don't change anything here — you read it. Each entry says what happened, when, and often a plain-English hint about how to fix it.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          When something isn't working and you (or IT) need to find out why. Filter to the errors, open one, and read the details. You can also export the list or clear out old entries.
        </p>
      </Section>

      <Section title="The screen">
        <Diagram
          frameLabel="Settings → System Diagnostics Log"
          legend={[
            <>Filter by category and how serious the entry is.</>,
            <>Search the text, or set a date range.</>,
            <>Click any row to open its full details.</>,
            <>Press <span className="text-white">Download</span> to export, or <span className="text-white">Acknowledge</span> to mark a row as handled.</>,
          ]}
        >
          <MockRow>
            <MockSelect label="Category" value="Email" marker={1} />
            <MockSelect label="Severity" value="Error + Critical" />
            <MockField label="Search" placeholder="message or details…" marker={2} />
          </MockRow>
          <MockTable
            columns={["When", "Category", "Severity", "Message"]}
            rows={[
              ["09:14", "email", <span className="text-red-300">Error</span>, "SMTP connection refused"],
              ["09:02", "api", <span className="text-amber-300">Warning</span>, "403 — missing permission"],
            ]}
          />
          <MockRow>
            <MockButton label="Open row" marker={3} variant="ghost" />
            <MockButton label="Download CSV / XLSX" marker={4} variant="ghost" />
            <MockButton label="Acknowledge" variant="ghost" />
          </MockRow>
        </Diagram>
      </Section>

      <Section title="Categories and severities">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1">
          <li>Categories: <span className="text-white">Email</span>, <span className="text-white">Auth</span> (sign-in), <span className="text-white">Database</span>, <span className="text-white">Scheduler</span>, <span className="text-white">API</span>, <span className="text-white">System</span>.</li>
          <li>Severities, from mild to serious: <span className="text-white">Info</span>, <span className="text-white">Warning</span>, <span className="text-white">Error</span>, <span className="text-white">Critical</span>.</li>
          <li>The red number badge in the top menu counts unread Error + Critical entries across all categories.</li>
        </ul>
      </Section>

      <Section title="The filters explained">
        <div className="space-y-2">
          <Field name="Categories / Severities" required="optional"><p>Tick chips to narrow the list. You can stack as many as you like.</p></Field>
          <Field name="Date range" required="optional"><p>From and To pickers. The "To" date is required before you're allowed to bulk-delete (a safety check).</p></Field>
          <Field name="Search" required="optional"><p>Matches words inside the message and the details. Special characters are handled safely.</p></Field>
          <Field name="Grouped view" required="optional"><p>Folds identical errors into one line showing how many times it happened, when it was first and last seen, and a link to the latest one.</p></Field>
          <Field name="Auto-refresh" required="optional"><p>Re-checks every 15 seconds when on. Turn it off if you're studying one frozen moment.</p></Field>
        </div>
      </Section>

      <Section title="What's inside an opened entry">
        <p className="text-sm text-white/65 leading-relaxed">Click a row to expand it. The most useful details are:</p>
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1 ml-2">
          <li><span className="text-white">app_version</span> — which build of the app it happened on.</li>
          <li><span className="text-white">request_id</span> — a unique tag put on every web request. It's also returned in the <span className="text-white">X-Request-Id</span> response header, so you can filter the log by it to see the whole story of one request.</li>
          <li><span className="text-white">first_app_frame</span> — points straight at the file and line that broke. Usually where the bug is.</li>
          <li><span className="text-white">troubleshooting_hint</span> — a plain-English suggestion for how to fix common problems.</li>
          <li><span className="text-white">request_body</span> / <span className="text-white">breadcrumbs</span> — what the user was doing. Passwords and secrets are scrubbed out.</li>
          <li><span className="text-white">user_id</span> + <span className="text-white">username</span> + <span className="text-white">ip</span> + <span className="text-white">user_agent</span> — who, where, and on what device.</li>
          <li><span className="text-white">acknowledged_by</span> + <span className="text-white">acknowledged_at</span> — once a tech marks the row handled, it records who and when. Handy for shift handovers.</li>
        </ul>
      </Section>

      <Section title="Related entries panel">
        <p className="text-sm text-white/65 leading-relaxed">
          At the bottom of an opened entry. It shows other entries from the same <span className="text-white">request_id</span> (or the same kind of error within ±10 minutes), so you can see the full trail for one request without searching by hand.
        </p>
      </Section>

      <Section title="Acknowledge, export, and clear out">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1">
          <li><span className="text-white">Acknowledge</span> a row to remove it from the red unread badge. It records who and when.</li>
          <li><span className="text-white">Download (CSV / XLSX)</span> exports exactly what your current filters show.</li>
          <li><span className="text-white">Bulk delete</span> removes only what matches the filter. A "To" date is required so a slip can't wipe the whole table, and it asks for your password.</li>
          <li>Anything older than <span className="text-white">90 days</span> is cleared out automatically each night.</li>
        </ul>
      </Section>

      <Section title="A quick fix-it cheat sheet">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1">
          <li><span className="text-white">database / error</span> "column ... does not exist" (<span className="text-white">42703</span>) → restart the server so it re-applies the missing database changes.</li>
          <li><span className="text-white">database / error</span> "unique violation" (<span className="text-white">23505</span>) → the user submitted with out-of-date data. Reproduce using the <span className="text-white">request_body</span>.</li>
          <li><span className="text-white">email / error</span> "connection refused" → the mail server address or port is wrong in Settings → Email.</li>
          <li><span className="text-white">email / error</span> "authentication failed" → the mailbox password changed; update it in Settings → Email.</li>
          <li><span className="text-white">api / warning</span> "403" → the user lacks a permission. Grant it in Roles &amp; Permissions.</li>
          <li><span className="text-white">api / warning</span> "400" / "422" → the submitted data failed a check. Compare <span className="text-white">request_body</span> with what the form expects.</li>
          <li><span className="text-white">system / critical</span> "uncaughtException" → the app may be unstable. Check the workflow and restart if needed.</li>
        </ul>
      </Section>

      <Section title="How to triage a problem">
        <Steps steps={[
          <>Open Settings → System Logs and filter Severity to Error or Critical.</>,
          <>Click a row to open it. Read the <span className="text-white">first_app_frame</span> and <span className="text-white">troubleshooting_hint</span> first.</>,
          <>If a user reported it, ask them for the <span className="text-white">request_id</span> (in the <span className="text-white">X-Request-Id</span> header) and filter to see the whole trail.</>,
          <>Reproduce using the <span className="text-white">request_body</span> / <span className="text-white">breadcrumbs</span>, fix it, then Acknowledge the row to clear the badge.</>,
        ]} />
      </Section>

      <Callout variant="info" title="The full runbook">
        A longer version of this guide ("Debugging from the System Diagnostics Log") lives in the project README (<span className="text-white">replit.md</span>). Print it for the IT desk if helpful.
      </Callout>
    </div>
  );
}
